import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { connect } from 'react-redux'
import Styles from './TopVouchersCard.module.css'
import { Colors } from 'Theme'
import PropTypes from 'prop-types'
import AnalyticsSelectors from 'Stores/Analytics/Selectors'
import AnalyticsActions from 'Stores/Analytics/Actions'
import IFText from 'Components/IFText/IFText'
import { useTranslation } from 'react-i18next'
import RequestState from 'Enums/RequestState'
import IFSkeleton from 'Components/IFSkeleton/IFSkeleton'
import IFTable from 'Components/IFTable/IFTable'
import ReportIcon from '@mui/icons-material/Report'
import { formatNumber } from 'Utils/Calculations'
import { margin } from '@mui/system'
const TopVouchersCard = ({
  fetchTopVouchers,
  topVouchers,
  filter,
  fetchTopVouchersRequestState,
}) => {
  const { t } = useTranslation()
  const tableRef = useRef(null)

  useEffect(() => {
    fetchTopVouchers(filter)
  }, [filter])

  const [failedRequest, setFailedRequest] = useState(false)
  useEffect(() => {
    if (
      fetchTopVouchersRequestState === RequestState.ERROR_0_NETWORK ||
      fetchTopVouchersRequestState === RequestState.ERROR_400_OCCURRED ||
      fetchTopVouchersRequestState === RequestState.ERROR_401_OCCURRED ||
      fetchTopVouchersRequestState === RequestState.ERROR_403_OCCURRED ||
      fetchTopVouchersRequestState === RequestState.ERROR_409_OCCURRED ||
      fetchTopVouchersRequestState === RequestState.ERROR_UNKNOWN_OCCURRED
    ) {
      setFailedRequest(true)
    }
    if (fetchTopVouchersRequestState === RequestState.SUCCEEDED) {
      setFailedRequest(false)
    }
  }, [fetchTopVouchersRequestState])

  const TopVouchersTableColumns = useMemo(
    () => [
      {
        accessorKey: 'name',
        enableEditing: false,
        size: 30,
        muiTableHeadCellProps: {
          sx: {
            backgroundColor: Colors.Analytics.AnalyticsDataCard,
            color: Colors.Analytics.AnalyticsDataGreyText,
          },
        },

        header:
          fetchTopVouchersRequestState === RequestState.LOADING ? (
            <div>
              <IFSkeleton
                variant="text"
                width="120px"
                animation="none"
                height={20}
              />
            </div>
          ) : (
            <div className={Styles.Title}>{t('Analytics.Voucher')}</div>
          ),
        Cell: ({ row }) => {
          return row.index < topVouchers?.data?.length ? (
            <div className={Styles.Title}>
              <IFText>{row.original.name}</IFText>
            </div>
          ) : failedRequest ? (
            <div>
              <ReportIcon
                width={28}
                height={28}
                style={{ color: Colors.red }}
              />
              <IFText>{t('IFTable.TryAgainText')}</IFText>
            </div>
          ) : (
            <IFSkeleton
              variant="text"
              width="120px"
              animation="none"
              height={20}
            />
          )
        },
      },
      {
        accessorKey: 'redeemedCount',
        enableEditing: false,
        size: 30,
        muiTableHeadCellProps: {
          align: 'center',
          sx: {
            backgroundColor: Colors.Analytics.AnalyticsDataCard,
            color: Colors.Analytics.AnalyticsDataGreyText,
            padding: '0 !important',
          },
        },
        muiTableBodyCellProps: {
          sx: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '0 !important',
          },
        },
        header:
          fetchTopVouchersRequestState === RequestState.LOADING ? (
            <div>
              <IFSkeleton
                variant="text"
                width="120px"
                animation="none"
                height={20}
              />
            </div>
          ) : (
            <div>{t('Analytics.RedeemedCount')}</div>
          ),
        Cell: ({ row }) => {
          return row.index < topVouchers?.data?.length ? (
            <div className={Styles.Header}>
              <IFText>{row.original.redeemedCount}</IFText>
            </div>
          ) : failedRequest ? (
            <div>
              <ReportIcon
                width={28}
                height={28}
                style={{ color: Colors.red }}
              />
              <IFText>{t('IFTable.TryAgainText')}</IFText>
            </div>
          ) : (
            <IFSkeleton
              variant="text"
              width="120px"
              animation="none"
              height={20}
            />
          )
        },
      },
      {
        accessorKey: 'averagePointsUsed',
        enableEditing: false,
        size: 50,
        muiTableHeadCellProps: {
          align: 'center',
          sx: {
            backgroundColor: Colors.Analytics.AnalyticsDataCard,
            color: Colors.Analytics.AnalyticsDataGreyText,
            padding: '0 !important',
          },
        },
        muiTableBodyCellProps: {
          sx: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '0 !important',
          },
        },
        header:
          fetchTopVouchersRequestState === RequestState.LOADING ? (
            <div>
              <IFSkeleton
                variant="text"
                width="120px"
                animation="none"
                height={20}
              />
            </div>
          ) : (
            <div>{t('Analytics.AveragePointsUsed')}</div>
          ),
        Cell: ({ row }) => {
          return row.index < topVouchers?.data?.length ? (
            <div className={Styles.Header}>
              <IFText>{row.original.averagePointsUsed}</IFText>
            </div>
          ) : failedRequest ? (
            <div>
              <ReportIcon
                width={28}
                height={28}
                style={{ color: Colors.red }}
              />
              <IFText>{t('IFTable.TryAgainText')}</IFText>
            </div>
          ) : (
            <IFSkeleton
              variant="text"
              width="120px"
              animation="none"
              height={20}
            />
          )
        },
      },
    ],
    [
      topVouchers,
      fetchTopVouchers,
      fetchTopVouchersRequestState,
      failedRequest,
    ],
  )
  const loadMoreData = () => {
    if (fetchTopVouchersRequestState === RequestState.LOADING) return
    fetchTopVouchers(filter)
  }
  const fetchMoreOnBottomReached = useCallback(
    (event) => {
      if (event) {
        const { scrollHeight, scrollTop, clientHeight } = event
        if (scrollHeight - scrollTop - clientHeight < 400) {
          loadMoreData()
        }
      }
    },
    [loadMoreData],
  )

  return (
    <IFTable
      columns={TopVouchersTableColumns}
      ref={tableRef}
      tableMaxHeight={486}
      filters={filter}
      tableRequestState={fetchTopVouchersRequestState}
      fetchMoreOnBottomReached={fetchMoreOnBottomReached}
      data={topVouchers.data}
      showSkeleton={
        fetchTopVouchersRequestState === RequestState.LOADING ||
        (fetchTopVouchersRequestState !== RequestState.SUCCEEDED &&
          topVouchers?.data.length === 0)
      }
    />
  )
}

const mapStateToProps = (state) => ({
  topVouchers: AnalyticsSelectors.getTopVouchers(state),
  fetchTopVouchersRequestState:
    AnalyticsSelectors.getFetchTopVouchersRequestState(state),
})

const mapDispatchToProps = (dispatch) => ({
  fetchTopVouchers: (filter) =>
    dispatch(AnalyticsActions.fetchTopVouchers(filter)),
})

TopVouchersCard.propTypes = {
  filter: PropTypes.object,
  topVouchers: PropTypes.object,
  fetchTopVouchers: PropTypes.func,
  fetchTopVouchersRequestState: PropTypes.number,
}

export default connect(mapStateToProps, mapDispatchToProps)(TopVouchersCard)
